import Vue from 'vue'
import App from './App.vue'
import router from './router'
import { version } from '../package.json'

// Icons
import 'vue-awesome/icons/cogs'
import 'vue-awesome/icons/volume-off'
import 'vue-awesome/icons/volume-up'
import 'vue-awesome/icons/sign-in-alt'
import 'vue-awesome/icons/sign-out-alt'
import Icon from 'vue-awesome/components/Icon.vue'
Vue.component('icon', Icon)

// Bootstrap
import BootstrapVue from 'bootstrap-vue'
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'
import './scss/custom.scss'
Vue.use(BootstrapVue)

// Plugins
import { TranslationService, KeybindingsService } from 'shared/services'
import { AnimationService } from 'display/services'
import * as Sentry from '@sentry/vue'
import TWEEN from '@tweenjs/tween.js'
Vue.use(TranslationService)
Vue.use(KeybindingsService)
Vue.use(AnimationService)

Vue.config.productionTip = false

import { Server } from 'shared/state'
import init from 'shared/init'
import { config } from 'display/config'

// After a new deploy, cached index.html may reference old chunk hashes that no longer exist.
// Reload once to pick up the new HTML — sessionStorage flag prevents an infinite reload loop.
window.addEventListener('unhandledrejection', event => {
	const reason = event.reason
	const isChunkError = reason?.code === 'CSS_CHUNK_LOAD_FAILED'
		|| /Loading chunk \S+ failed/i.test(reason?.message)
	if (isChunkError && !sessionStorage.getItem('chunk_reload')) {
		sessionStorage.setItem('chunk_reload', '1')
		window.location.reload()
	}
})

// Local dev (npm run start) always has NODE_ENV === 'development' at build time, regardless of
// what URL later serves the page — unlike a hostname check, this still reports correctly from
// the production desktop/kiosk build even when that's served from a bare localhost URL (see the
// host_environment context below).
if (process.env.NODE_ENV === 'production') {
Sentry.init({
	Vue,
	dsn: 'https://6fea6fc67823f6bc86f937698bee7d39@o4504238250983424.ingest.sentry.io/4506378157359104',
	release: `ergrace-display@${version}`,
  	environment: process.env.NODE_ENV,
	ignoreErrors: [
		// Browser autoplay policy — expected, not a bug
		/NotAllowedError.*play\(\)/,
		// Instagram's in-app browser (iabjs://) own WebView JS-to-Java bridge,
		// firing on beforeunload after Android has already torn down the native
		// object. Not our code — nothing on our domain is in this stack trace.
		// Matches whichever bridge method it was mid-call on (postMessage,
		// enableDidUserTypeOnKeyboardLogging, etc.) — same teardown race, not
		// worth a separate entry per method name.
		/Error invoking \w+: Java object is gone/,
		// Same Instagram in-app-browser logger, iOS/WKWebView variant: calls
		// window.webkit.messageHandlers on page hide after the native bridge
		// has already been torn down. Also not our code.
		/window\.webkit\.messageHandlers/,
	],
	// Google Sheets leaderboard publish (_LdbdPage.vue) is already treated as
	// best-effort — failures are caught and logged client-side, never shown to
	// the user. Transient network errors during that sync surface as unhandled
	// rejections from inside the google-spreadsheet/gaxios library's own
	// internals (no frames from our code), so there's nothing to catch —
	// drop them at the Sentry boundary instead of chasing library internals.
	beforeSend(event, hint) {
		const message = hint?.originalException instanceof Error
			? hint.originalException.message
			: event.message
		const isNetworkNoise = message === 'Network Error' || message === 'Failed to fetch'
		const hitGoogleSheets = event.breadcrumbs?.some(b =>
			(b.category === 'xhr' || b.category === 'fetch') &&
			typeof b.data?.url === 'string' &&
			b.data.url.includes('googleapis.com')
		)
		if (isNetworkNoise && hitGoogleSheets) { return null }

		// Same situation for the elimination-race voice announcements
		// (Speech.vue's definitionChanged): a burst of parallel AWS Polly
		// synthesizeSpeech presign calls, one per boat. A non-Error promise
		// rejection with no stack trace from inside the AWS SDK's own
		// internals during that burst isn't reachable from our code either.
		const isNonErrorRejection = /^Non-Error promise rejection captured with value/.test(message ?? '')
		const hitPollySynthesize = event.breadcrumbs?.some(b =>
			b.category === 'console' &&
			typeof b.message === 'string' &&
			b.message.includes('synthesizeSpeech')
		)
		if (isNonErrorRejection && hitPollySynthesize) { return null }

		return event
	},
	integrations: [
	  new Sentry.BrowserTracing(),
	  new Sentry.BrowserProfilingIntegration(),
	  new Sentry.Replay(),
	],
	tracesSampleRate: 1.0,
	// @ts-ignore
	replay: { enabled: true, screenshot: true, },
	// @ts-ignore
	replaysSessionSampleRate: parseFloat(process.env.VUE_APP_replaysSessionSampleRate),
	// @ts-ignore
	replaysOnErrorSampleRate: parseFloat(process.env.VUE_APP_replaysOnErrorSampleRate),
})
}

// Several production crash reports have come from pages served at a bare
// localhost URL with a real, geolocated public IP attached — consistent with
// this build running inside something else (an embedded/local web server,
// an iframe, Electron's renderer) rather than loaded directly. Captured once
// here so a future report can confirm or rule that out instead of guessing
// from the URL alone.
Sentry.setContext('host_environment', {
	inIframe: window.top !== window.self,
	electron: !!(window as any).process?.versions?.electron,
	referrer: document.referrer || null,
	outerWidth: window.outerWidth,
	outerHeight: window.outerHeight,
	innerWidth: window.innerWidth,
	innerHeight: window.innerHeight,
})

// A Sort instance destroyed mid-animation without .stop() leaks its tween into
// this shared group forever (see Sort.ts/ParticipantView.vue fixes) — normal
// active count across all live views at once is a handful at most. Attaching
// the live count to every report catches this for ANY future crash, not just
// a RangeError, and the one-time warning below surfaces a building leak long
// before it could ever grow large enough to actually crash anything.
let tweenLeakReported = false
setInterval(() => {
	const count = TWEEN.getAll().length
	Sentry.setContext('tween_group', { count })
	if (count > 50 && !tweenLeakReported) {
		tweenLeakReported = true
		Sentry.captureMessage(`Tween group size abnormally large: ${count} active tweens`, 'warning')
	}
}, 60_000)

init(Vue, Server).then(env => {
	config.init(env)
	new Vue({
		router,
		render: (h) => h(App),
	}).$mount('#app')
})
